import React, { useEffect } from 'react';
import { X, Github, ExternalLink, Calendar, Users, Star } from 'lucide-react';

const ProjectModal = ({ project, isOpen, onClose }) => {
  useEffect(() => {
    if (!isOpen) return;
    document.body.style.overflow = 'hidden';
    const onEsc = (e) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onEsc);
    return () => {
      document.body.style.overflow = 'unset';
      window.removeEventListener('keydown', onEsc);
    };
  }, [isOpen, onClose]);

  if (!isOpen || !project) return null;

  const detailsMap = {
    15: {
      longDescription:
        'A publicly deployed full-stack cooperative multiplayer game with two modes: Tiny Heroes, a family-friendly collection of ten adventures, and Signal Lost, a five-sector sci-fi mission. Built around real-time room state, private player clues, coordinated decisions, and reconnect support.',
      features: [
        'Real-time cooperative rooms for 2–6 players',
        'Ten Tiny Heroes adventures and five Signal Lost sectors',
        'Role-specific private clues and shared team decisions',
        'Server-authoritative multiplayer state and synchronization',
        'Reconnect and refresh recovery',
        'Public live game deployed on Render',
      ],
      status: 'Live',
    },
    13: {
      longDescription:
        'A production website for BRYZ Cleaning Experts, presenting upholstery and leather care services with an AI concierge.',
      features: [
        'Production cleaning-service website',
        'AI concierge',
        'Upholstery and leather care service pages',
      ],
      status: 'Production',
    },
    14: {
      longDescription:
        'Production voice orchestration connects RingCentral, LiveKit, and OpenAI Realtime through a TypeScript/Fastify backend. The linked public repository recreates selected patterns with mock adapters and synthetic inputs; it does not connect to live providers.',
      features: [
        'Voice-session lifecycle and intent routing',
        'Staff handoffs and human escalation',
        'Post-call workflow orchestration',
        'Mock-provider demo with synthetic inputs',
        'Transfer idempotency and fallback behavior',
      ],
      status: 'Public demo (mock providers)',
    },
    5: {
      longDescription:
        'A comprehensive AI image generation suite built with ComfyUI that revolutionizes the creative workflow. This project features advanced custom nodes, automated batch processing, and intelligent workflow optimization that reduces generation time by 60%.',
      features: [
        'Custom ComfyUI nodes for advanced image manipulation',
        'Automated batch processing with queue management',
        'Real-time workflow optimization and monitoring',
        'Integration with multiple AI models (SDXL, ControlNet, etc.)',
        'Advanced prompt engineering tools',
        'Cloud-based rendering and storage',
      ],
      technologies: ['ComfyUI', 'Python', 'PyTorch', 'CUDA', 'Redis', 'Docker'],
      timeline: '6 months',
      team: 'Solo project',
      status: 'Production',
      gallery: project.gallery && project.gallery.length > 0 ? project.gallery : [project.image],
    },
  };

  const details = detailsMap[project.id] || {};

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="relative">
          <img src={project.image} alt={project.title} className="w-full h-56 object-cover" />
          <button
            onClick={onClose}
            className="absolute top-3 right-3 p-2 bg-white/80 rounded-full hover:bg-white"
            aria-label="Close"
          >
            <X size={20} />
          </button>
          <div className="absolute bottom-4 left-4 right-4">
            <h2 className="text-2xl font-bold text-white drop-shadow">{project.title}</h2>
            <p className="text-white drop-shadow-sm">{project.description}</p>
          </div>
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent pointer-events-none" />
        </div>

        {/* Body */}
        <div className="p-5">
          {/* Actions */}
          <div className="flex flex-wrap gap-3 mb-5">
            {project.githubUrl && project.githubUrl !== '#' && (
              <a
                href={project.githubUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-4 py-2 bg-gray-900 text-white rounded-full"
              >
                <Github size={18} /> View Code
              </a>
            )}
            {project.liveUrl && project.liveUrl !== '#' && (
              <a
                href={project.liveUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-full"
              >
                <ExternalLink size={18} /> Live Demo
              </a>
            )}
          </div>

          {/* Info Grid */}
          <div className="grid md:grid-cols-3 gap-3 mb-6">
            <div className="bg-gray-50 rounded-xl p-3">
              <div className="flex items-center gap-2 font-semibold mb-1">
                <Calendar size={16} className="text-blue-600" /> Timeline
              </div>
              <div>{details.timeline || '—'}</div>
            </div>
            <div className="bg-gray-50 rounded-xl p-3">
              <div className="flex items-center gap-2 font-semibold mb-1">
                <Users size={16} className="text-purple-600" /> Team
              </div>
              <div>{details.team || '—'}</div>
            </div>
            <div className="bg-gray-50 rounded-xl p-3">
              <div className="flex items-center gap-2 font-semibold mb-1">
                <Star size={16} className="text-yellow-500" /> Status
              </div>
              <div>{details.status || '—'}</div>
            </div>
          </div>

          {/* Overview */}
          {details.longDescription && (
            <div className="mb-5">
              <h3 className="text-lg font-bold mb-2">Project Overview</h3>
              <p className="text-gray-700 leading-relaxed">{details.longDescription}</p>
            </div>
          )}

          {/* Features */}
          {details.features && (
            <div className="mb-5">
              <h3 className="text-lg font-bold mb-2">Key Features</h3>
              <ul className="space-y-1 list-disc pl-5">
                {details.features.map((f) => (
                  <li key={f} className="text-gray-700">{f}</li>
                ))}
              </ul>
            </div>
          )}

          {/* Technologies */}
          <div className="mb-2">
            <h3 className="text-lg font-bold mb-2">Technologies Used</h3>
            <div className="flex flex-wrap gap-2">
              {(details.technologies || project.technologies || []).map((t) => (
                <span key={t} className="px-3 py-1 bg-gray-100 rounded-full text-sm">{t}</span>
              ))}
            </div>
          </div>

          {/* Gallery */}
          {details.gallery && details.gallery.length > 1 && (
            <div className="mt-4">
              <h3 className="text-lg font-bold mb-2">Project Gallery</h3>
              <div className="grid sm:grid-cols-2 gap-3">
                {details.gallery.map((src, idx) => (
                  <img
                    key={idx}
                    src={src}
                    alt={`${project.title} ${idx + 1}`}
                    className="w-full h-40 object-cover rounded-xl"
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProjectModal;
